import React, { useState, useRef } from 'react';
import { Upload, X, FileText, AlertCircle, CheckCircle, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { FileViewer } from './FileViewer';

interface FileUploadProps {
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // in MB
  onUpload?: (files: File[]) => void;
  onSuccess?: (results: LoadedFile[]) => void;
  onError?: (error: string) => void;
  className?: string;
  disabled?: boolean;
}

interface LoadedFile {
  filename: string;
  size: number;
  type: string;
  content?: string | ArrayBuffer;
  lastModified: number;
}

export function FileUpload({
  accept = '*',
  multiple = false,
  maxSize = 100,
  onUpload,
  onSuccess,
  onError,
  className = '',
  disabled = false
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedFiles, setLoadedFiles] = useState<LoadedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      return `File ${file.name} is too large. Maximum size is ${maxSize}MB.`;
    }

    // Check file type if specified
    if (accept !== '*' && !accept.includes(file.type) && !accept.includes(`.${file.name.split('.').pop()}`)) {
      return `File ${file.name} is not an accepted file type.`;
    }

    return null;
  };

  const readFileContent = (file: File): Promise<string | ArrayBuffer> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        resolve(reader.result as string | ArrayBuffer);
      };
      
      reader.onerror = () => {
        reject(new Error(`Failed to read file ${file.name}`));
      };
      
      // Choose the appropriate reading method based on file type
      if (file.type.startsWith('text/') || file.name.endsWith('.csv') || file.name.endsWith('.json')) {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    });
  };

  const handleFiles = async (files: FileList) => {
    const fileArray = Array.from(files);
    setError(null);

    // Validate files
    for (const file of fileArray) {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    if (onUpload) {
      onUpload(fileArray);
    }

    // Load files locally
    setIsLoading(true);
    try {
      const loadPromises = fileArray.map(async (file) => {
        const content = await readFileContent(file);
        return {
          filename: file.name,
          size: file.size,
          type: file.type,
          content: content,
          lastModified: file.lastModified,
        };
      });

      const results = await Promise.all(loadPromises);
      setLoadedFiles(prev => [...prev, ...results]);
      
      if (onSuccess) {
        onSuccess(results);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'File loading failed';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (disabled || isLoading) return;
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  const removeFile = (index: number) => {
    setLoadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handlePreview = (loadedFile: LoadedFile) => {
    // Create a File object from the loaded file data
    if (loadedFile.content) {
      const blob = new Blob([loadedFile.content], { type: loadedFile.type });
      const file = new File([blob], loadedFile.filename, { type: loadedFile.type });
      setPreviewFile(file);
      setIsPreviewOpen(true);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragging
            ? 'border-blue-500 bg-blue-50'
            : error
            ? 'border-red-300 bg-red-50'
            : 'border-gray-300 hover:border-gray-400'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !disabled && !isLoading && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={accept}
          multiple={multiple}
          onChange={handleFileSelect}
          disabled={disabled || isLoading}
        />

        <div className="space-y-2">
          <Upload className={`h-8 w-8 mx-auto ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
          <div>
            <p className="text-sm font-medium text-gray-900">
              {isLoading ? 'Loading files...' : 'Drop files here or click to browse'}
            </p>
            <p className="text-xs text-gray-500">
              {accept !== '*' && `Accepted formats: ${accept}`}
              {maxSize && ` • Max size: ${maxSize}MB`}
            </p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-4 w-4 text-red-500" />
          <span className="text-sm text-red-700">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Loaded Files */}
      {loadedFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-900">Loaded Files</h4>
          {loadedFiles.map((file, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
            >
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-4 w-4 text-green-500" />
                  <FileText className="h-4 w-4 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{file.filename}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePreview(file)}
                  className="px-3 py-1 text-sm bg-blue-500 text-white hover:bg-blue-700 rounded"
                  title="Preview file"
                >
                  Preview
                </button>
                <button
                  onClick={() => removeFile(index)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Loading Progress */}
      {isLoading && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Loading files...</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }} />
          </div>
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Eye className="h-5 w-5" />
              <span>File Preview: {previewFile?.name}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {previewFile && (
              <FileViewer
                file={previewFile}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}